import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const faqCategories = [
  {
    name: 'Product',
    questions: [
      {
        q: 'What position limits does PositionIQ support?',
        a: 'PositionIQ supports all CFTC-regulated position limits including federal speculative position limits, exchange-set limits, and accountability levels across all commodity futures and options contracts.',
      },
      {
        q: 'How does position aggregation work?',
        a: 'We automatically aggregate positions across all accounts you control or have ownership interest in, following CFTC aggregation rules. This includes accounts at multiple FCMs, proprietary accounts, and accounts of entities you own or control.',
      },
      {
        q: 'How often are positions updated?',
        a: 'Depending on your plan, positions can be updated daily (Starter), hourly, or in real-time as trades execute (Professional and Enterprise). Real-time plans include streaming data integration with major exchanges.',
      },
      {
        q: 'Can I export compliance reports?',
        a: 'Yes, you can export reports in PDF, Excel, and CSV formats. We also support automated scheduled report delivery to your team or regulators.',
      },
    ],
  },
  {
    name: 'Security',
    questions: [
      {
        q: 'How is my data protected?',
        a: 'We use bank-level AES-256 encryption for data at rest and TLS 1.3 for data in transit. All data is stored in SOC 2 Type II compliant data centers with 24/7 monitoring.',
      },
      {
        q: 'Where is my data stored?',
        a: 'Data is stored in secure, redundant data centers in the United States. Enterprise customers can choose specific geographic regions or on-premise deployment options.',
      },
      {
        q: 'Who can access my data?',
        a: 'Only authorized users within your organization can access your data. We implement role-based access control (RBAC) and support SSO integration. Our staff access is strictly limited and audited.',
      },
    ],
  },
  {
    name: 'Getting Started',
    questions: [
      {
        q: 'How long does implementation take?',
        a: 'Most customers are up and running within 1-2 weeks. This includes FCM integration, historical data import, and team training. Enterprise implementations may take 4-6 weeks depending on complexity.',
      },
      {
        q: 'Do you offer a free trial?',
        a: 'We offer a 14-day free trial of our Professional plan. You can also schedule a demo to see the platform in action before committing.',
      },
      {
        q: 'What support is available?',
        a: 'All plans include email support. Professional plans add priority support with faster response times. Enterprise customers get dedicated support representatives and 24/7 phone support.',
      },
      {
        q: 'Can I switch plans later?',
        a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.',
      },
    ],
  },
];

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cn(
      "border-b border-slate-100 last:border-0 transition-colors",
      isOpen && "bg-purple-50"
    )}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-5 text-left group"
      >
        <span className={cn(
          "font-medium pr-4 transition-colors text-slate-700",
          isOpen && "text-purple-700"
        )}>{question}</span>
        <div className={cn(
          "p-1 rounded-full transition-all",
          isOpen ? "bg-purple-100 rotate-180" : "bg-slate-100 group-hover:bg-purple-50"
        )}>
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-colors',
              isOpen ? 'text-purple-600' : 'text-slate-400'
            )}
          />
        </div>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-96 pb-5' : 'max-h-0'
        )}
      >
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export function FAQSection() {
  const [activeCategory, setActiveCategory] = useState('Product');

  return (
    <section id="faq" className="relative py-20 md:py-28 bg-white">
      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-4 py-1.5 text-sm mb-6">
            <HelpCircle className="h-4 w-4 text-slate-600" />
            <span className="text-slate-700 font-medium">FAQ</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-slate-900">
            Frequently Asked{' '}
            <span className="text-purple-600">
              Questions
            </span>
          </h2>
          <p className="text-lg text-slate-600">
            Everything you need to know about PositionIQ. Can't find what you're looking for?{' '}
            <a href="#demo" className="text-purple-600 font-medium hover:underline">
              Contact our team
            </a>
            .
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          {faqCategories.map((category) => (
            <button
              key={category.name}
              onClick={() => setActiveCategory(category.name)}
              className={cn(
                'px-5 py-2.5 rounded-full text-sm font-medium transition-colors',
                activeCategory === category.name
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-purple-300 hover:bg-purple-50'
              )}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
            {faqCategories
              .find((c) => c.name === activeCategory)
              ?.questions.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.q}
                  answer={faq.a}
                />
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
